import { defineStore } from "pinia";
import { ref, computed } from "vue";
import type { PostProcessingWatcher } from "@cc-consulting-nv/ccsdk";
import { getSdk } from "@/composables/useSdk";

/**
 * Tracks posts that the API is still processing after create.
 *
 * Owns nothing about the polling/race logic — that lives in
 * `sdk.watchPostProcessing()`. This store is a thin reactive shell:
 *
 *  - `add(ulid)` starts a watcher; `remove(ulid)` cancels.
 *  - `markComplete(ulid)` is what the realtime handler calls when
 *    a `PostProcessingComplete` Pusher event arrives — the SDK
 *    races it against the next poll tick.
 *  - `count` / `hasProcessing` drive UI state in the header.
 */

export const useProcessingPostsStore = defineStore("processingPosts", () => {
  const watchers = ref<Map<string, PostProcessingWatcher>>(new Map());

  const ulids = computed(() => Array.from(watchers.value.keys()));
  const count = computed(() => watchers.value.size);
  const hasProcessing = computed(() => watchers.value.size > 0);

  function rebind(): void {
    // Replace map ref so destructured consumers re-render.
    watchers.value = new Map(watchers.value);
  }

  function dropEntry(ulid: string): void {
    if (!watchers.value.delete(ulid)) return;
    rebind();
  }

  function add(ulid: string): void {
    if (!ulid || watchers.value.has(ulid)) return;
    const watcher = getSdk().watchPostProcessing(ulid, {
      onDone: () => dropEntry(ulid),
      onError: (err) => {
        console.warn("[processingPosts] watcher error for", ulid, err);
      },
    });
    watchers.value.set(ulid, watcher);
    rebind();
  }

  function remove(ulid: string): void {
    const watcher = watchers.value.get(ulid);
    if (!watcher) return;
    watcher.stop();
    dropEntry(ulid);
  }

  /**
   * Fast-clear from a realtime signal. The SDK watcher races this
   * against polling and fires onDone exactly once.
   */
  function markComplete(ulid: string): void {
    const watcher = watchers.value.get(ulid);
    if (!watcher) return;
    watcher.markComplete();
  }

  function clear(): void {
    watchers.value.forEach((w) => w.stop());
    watchers.value = new Map();
  }

  return {
    ulids,
    count,
    hasProcessing,
    add,
    remove,
    markComplete,
    clear,
  };
});
